import {CatalogItemsPanelProps, useGroupByGroupStore, GroupByGroupStore, BuilderCardWithPopoverContainer, ProductsVirtualRowStore, useLoadMoreProductsVirtualRowStore, useVipLocalStore, LoadingIndicator} from '@modules'
import {LoadMoreCatalogItemsButton, LoadMoreItemsButton} from '@modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByCatalog_CatalogItems/CardView/LoadMoreProductCatalog/LoadMoreCatalogItemsButton'
import {Fragment, useContext, useEffect} from 'react'
import {LoadMoreProductsVirtualRowStoreContext, useCatalogItemsCardViewStore} from './'
import {computed, action, observable, trace, reaction, toJS} from 'mobx'
import * as css from './CatalogItemsCardView.css'
import {prettyPrint, StoresInstance} from '@stores'
import {React, observer, useAsObservableSource} from '@api'
import {VipLocalStore} from '@modules'
import {ErrorCard} from 'centric-ui-components'

export const DEBUG_CATALOG_ITEM_CARDS = DEBUG && false

export interface CatalogItemsCardViewRowProps extends Partial<CatalogItemsPanelProps> {
  index: number
}

export const LoadMoreProductsVirtualRow = observer<CatalogItemsCardViewRowProps>(props => {
  const loadMoreStore = useCatalogItemsCardViewStore(),
    obsProps = useAsObservableSource(props),
    store = useVipLocalStore((ws) => new ProductsVirtualRowStore(ws, obsProps, loadMoreStore)),
    {
      catalog: {perspective}, catalog
    } = store,
    {itemsPerClick, isLoadingMore} = loadMoreStore,
    {props: {onlineItemCount, index}, error, itemWrappers} = store

  useEffect(() => store.registerEffects(), [])
  //console.log({index, props, searchPayload})
  //console.log({loading, data, levelOfDetail, error}, loading ? 'skeletons' : 'real cards')

  let showLoadMoreButton = store.showLoadMoreButton

  if (itemWrappers && itemWrappers.length == 0 && (props.onlineItemCount > itemWrappers.length)) {
    if (!catalog.loadingCounts && !store.loading) {
      console.warn(`Expected ${Math.min(itemsPerClick, props.onlineItemCount)} catalog items, received ${itemWrappers.length} in product virtual row query.  There is likely an issue with your query or with the catalog item count.`, JSON.stringify(_.pick(props, ['index', 'level', 'offline', 'offlineItemCount', 'onlineItemCount', 'path']), null, 1))
      console.debug(prettyPrint(store.searchPayload))
    }

    showLoadMoreButton = false
  }

  // useEffect(() => {
  //   console.log(`Mount LoadMoreProductsVirtualRow`, toJS(props))
  //   return () => console.log(`Unmount LoadMoreProductsVirtualRow`)
  // }, [])


  //< flipKey={products?.length ? products[0].id : ''} className={css.flip}>

  //console.log(loading, itemWrappers)

  return <LoadMoreProductsVirtualRowStoreContext.Provider value={store}>
    {_.isEmpty(itemWrappers) ? null
                             : error ? <ErrorCard error={error}/>
                                     : <ProductVirtualRow items={itemWrappers} onClickProduct={loadMoreStore.onClickCard} loading={store.loading} {...props} itemsPerClick={itemsPerClick}>
                                 {props.children}
                               </ProductVirtualRow>}
    {showLoadMoreButton
     && (!perspective?.field || isLoadingMore) // don't show the button here if we are a perspective field as we will have sub virtual rows but DO show it during the initial load
     && <LoadMoreCatalogItemsButton className={css.loadMoreButton} count={onlineItemCount} index={index}/>}
  </LoadMoreProductsVirtualRowStoreContext.Provider>
})
LoadMoreProductsVirtualRow.displayName = 'LoadMoreProductsVirtualRow'
LoadMoreProductsVirtualRow['whyDidYouRender'] = true
LoadMoreProductsVirtualRow.defaultProps = {
  index: 0
}

type ProductVirtualRowProps = { items, onClickProduct } & Partial<CatalogItemsCardViewRowProps>

export const ProductVirtualRowStoreContext = React.createContext<ProductVirtualRowStore>(undefined)
export const useProductVirtualRowStore = () => useContext(ProductVirtualRowStoreContext)

class ProductVirtualRowStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: ProductVirtualRowProps, public groupStore: GroupByGroupStore, public virtualRowStore: ProductsVirtualRowStore) {
    super(stores)
  }

  /**
   * If we have nested values (colorways for MPV for example) it can result in 100 MPVs -> 1000s of colorways
   * We want to show a load more button and avoid rendering 1000s of items
   */
  @observable timesClicked = 1

  @action loadMore = () => {
    this.timesClicked++
  }

  @computed get maxTimesClicked() {
    return Math.floor(this.childrenCards.length / this.props.itemsPerClick)
  }

  registerEffects = () => {
    const toDispose = [
      reaction(() => this.perspective, () => {
        this.changingPerspectives = true
        this._debounceClearChangingPerspectives()
      })
    ]
    return () => toDispose.forEach(f => f())
  }

  _debounceClearChangingPerspectives = _.debounce(() => this.changingPerspectives = false, 500)

  @computed get productCardProps() {
    const {catalog: {_fieldByName, index: catalogIndex, workspaceByIndex}, perspective} = this,
      {props: {onClickProduct, path}, virtualRowStore} = this,
      index = (perspective?.field ? _fieldByName[perspective.field]?.esIndex : catalogIndex) ?? catalogIndex

    return {
      onClickProduct,
      path,
      type: index,
      workspace: workspaceByIndex[index],
      visibleCardFields: virtualRowStore?.store?.props?.assortment?.visibleCardFields
    }
  }

  @computed get perspective() {
    const {groupStore, catalog, virtualRowStore} = this
    if (virtualRowStore?.store?.props?.assortment?.selectedPerspective) {
      return virtualRowStore.store.props.assortment.selectedPerspective
    }
    if (groupStore?.settings?.perspective) {
      return catalog.perspectivesById[groupStore.settings.perspective]
    } else {
      return catalog.perspective
    }
  }

  @observable changingPerspectives = false

  @computed get childrenCards() {
    // trace()
    const {
        props: {offline, index, itemsPerClick}, productCardProps, groupStore, perspective, virtualRowStore, catalog,
        catalog: {settings: {showOnlyItemsWithChanges}}, filter: {offlineFilter}, es, filter
      } = this,
      perspectiveFieldName = virtualRowStore?.store?.props?.assortment?.selectedPerspective?.field ?? perspective?.field,
      perspectiveField = perspectiveFieldName ? catalog._fieldByName[perspectiveFieldName] : null

    // if ((groupStore?.settings?.showOnlyOfflineItems || showOnlyOfflineItems) && !offline) return []
    // if (groupStore?.settings?.showOnlyOnlineItems && offline) return []
    if (!offline && offlineFilter && offlineFilter.selected.has('offline') && !offlineFilter.selected.has('online')) return []

    let items = this.props.items
    if (showOnlyItemsWithChanges) {
      items = items.filter(item => catalog.catalogItemsWithUnsavedChangesMap[item._id])
    }

    const flattened = _.flatMap<{ key: string, item?: any, extraCardProps? }>(
        items.map((item, i) => {
          if (!perspectiveField) {
            return {item, key: `${item._id}_${i}`, index: (index * itemsPerClick) + i, extraCardProps: {index: i}}
          } else {
            // Transform the nested field (which may be an array) into a new set of card(s)
            const {val: nestedObject} = es.readField(item, perspectiveFieldName, null, {noString: true})

            // We don't want to have to convert all the existing complex things like style -> colorway to nested types in ES yet, so when user does something like filter active colorway = true, we do that filtering here for now - TODO PERF
            const arr = _.uniqBy(
              filter.filterNested(perspectiveFieldName, (_.isArray(nestedObject) ? nestedObject : [nestedObject]), groupStore/*, perspective*/),
              r => catalog.idFor(r)
            )

            // DEBUG && console.debug(`Nested items for `, item, {perspectiveFieldName, arr})

            const r = !nestedObject ? [] : arr.map((nestedObject, j) => {

              if (!perspectiveField.esIndex) {
                return {key: `${item._id}-${nestedObject?._id ?? nestedObject?.exId ?? nestedObject?.id}-${j}`, item: null}
              } else {
                return {
                  key: `${item._id}-${nestedObject?._id ?? nestedObject?.exId ?? nestedObject?.id}-${j}`,
                  item: nestedObject,
                  extraCardProps: {index: i, nested: {field: perspectiveField, parentData: item, data: nestedObject, arrayIndex: j}}
                }
              }
            })

            return r
          }
        })
      ).filter(_.identity),
      uniq = _.uniqBy(flattened, v => catalog.idFor(v.item, false))

    const r = true ?
              uniq.map(({key, item, extraCardProps}, i) => (
                !item
                ? <div className={css.noNested} key={i}/>
                : (
                  <BuilderCardWithPopoverContainer
                    key={key}
                    item={item}
                    data-index={extraCardProps?.index}
                    data-internal-index={i}
                    data-is-refetch={virtualRowStore?.isRefetchChange === true}
                    data-loading-data={virtualRowStore?.loading === true}
                    data-virtual-row-index={index}
                    productCardProps={productCardProps}
                    extraCardProps={extraCardProps}/>
                )))
                   : _.flatMap(
        items.map((item, i) => {
          const catalogItemCard = <BuilderCardWithPopoverContainer
            key={`${item._id}_${i}`}
            data-index={(index * itemsPerClick) + i}
            data-internal-index={i}
            data-loading-data={virtualRowStore?.loading === true}
            data-virtual-row-index={index}
            {...{
              item,
              productCardProps,
              extraCardProps: {index: i}
            }}/>

          if (!perspectiveField) {
            return catalogItemCard
          } else {
            // Transform the nested field (which may be an array) into a new set of card(s)

            const {val: nestedObject, valAsString} = es.readField(item, perspectiveFieldName)

            // We don't want to have to convert all the existing complex things like style -> colorway to nested types in ES yet, so when user does something like filter active colorway = true, we do that filtering here for now - TODO PERF
            const arr = _.uniqBy(
              filter.filterNested(perspectiveFieldName, (_.isArray(nestedObject) ? nestedObject : [nestedObject]), groupStore/*, perspective*/),
              r => catalog.idFor(r)
            )

            //DEBUG && console.debug(`Nested items for `, item, {perspectiveFieldName, arr})

            const r = !nestedObject
                      ? <div className={css.noNested} key={i} data-id={item._id}>
                        {/*No {perspective.label} for {boardItem.subtitle1 ?? boardItem.title*/}
                        {false && catalogItemCard}
                      </div>
                      : arr.map((nestedObject, j) => {

                if (!perspectiveField.esIndex) {
                  return <div className={css.nonCatalogItem}>
                    {valAsString}
                  </div>
                }

                const builderCardProps = {
                  item: nestedObject,
                  productCardProps,
                  extraCardProps: {index: i, nested: {field: perspectiveField, parentData: item, data: nestedObject, arrayIndex: j}}
                }

                // Generate a new synthetic version with only this instance of the item:
                return <BuilderCardWithPopoverContainer
                  key={`${item._id}-${nestedObject?._id ?? nestedObject?.exId ?? nestedObject?.id}-${j}`}
                  data-index={j}
                  {...builderCardProps}/>
              })

            return r
          }

          return null
        })).filter(_.identity)

    //DEBUG && perspectiveField && console.debug(`Nested items for ${index} - ${perspectiveField?.field} `, {in: toJS(items), out: r})

    // if (_.isEmpty(r)) {
    //   return [<div key={'empty_filter_html'}>All {pluralize(perspective.label)} have been filtered by your view options.</div>]
    // }

    return r
  }
}

export const ProductVirtualRow = observer<ProductVirtualRowProps>(function ProductVirtualRow(props) {
  const {items, itemsPerClick} = props,
    obsProps = useAsObservableSource(props),
    groupStore = useGroupByGroupStore(),
    virtualRowStore = useLoadMoreProductsVirtualRowStore(),
    store = useVipLocalStore((ws) => new ProductVirtualRowStore(ws, obsProps, groupStore, virtualRowStore)),
    {childrenCards, perspective, changingPerspectives} = store

  useEffect(() => store.registerEffects(), [])

  //console.debug(`Rendering catalog item cards for virtual row ${props.index}`, {childrenCards, props})
  return <ProductVirtualRowStoreContext.Provider value={store}>
    {changingPerspectives ? props.index === 0 ? <LoadingIndicator className={css.loadingPerspective} text='Changing Perspective...' /> : null
     : !items?.length
     ? <div>No results</div>
      // This rapidly gets very large as an MPV -> Colorway can result in massive explosion in # of card
     : !perspective?.field
       ? childrenCards
       : _.chunk(childrenCards.slice(), itemsPerClick).map((subRows, i) => <ProductVirtualRowNested items={subRows} index={i} key={i}/>)}
  </ProductVirtualRowStoreContext.Provider>
})


ProductVirtualRow['whyDidYouRender'] = true
ProductVirtualRow.defaultProps = {
  index: 0
}

export const ProductVirtualRowNested = observer<{ items: React.ReactNode, index: number }>(function ProductVirtualRowNested({items, index}) {
  const {props, props: {itemsPerClick}, timesClicked, maxTimesClicked, loadMore} = useProductVirtualRowStore(),
    loadMoreStore = useLoadMoreProductsVirtualRowStore()  // may be null if for Pending Items

  // console.log({loadMoreStore, itemsPerClick, items, timesClicked, maxTimesClicked, index})
  return <Fragment>
    {index < timesClicked
     ? items
     : timesClicked === index && index < maxTimesClicked
       ? <LoadMoreItemsButton onClick={loadMore}/>
       : null}
    {loadMoreStore?.showLoadMoreButton && index <= maxTimesClicked && timesClicked == maxTimesClicked && <LoadMoreCatalogItemsButton count={props.onlineItemCount} index={props.index}/>}
  </Fragment>
})



// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByCatalog_CatalogItems/CardView/LoadMoreProductCatalog/LoadMoreProductsVirtualRow.tsx